import React, { AnchorHTMLAttributes, CSSProperties, FunctionComponent, PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import { Icon, IconName } from '../icons/icon/icon-component';
import styles from './badge-component.module.scss';

type BadgeProps = PropsWithChildren<
  {
    icon?: IconName;
    colour?: 'yellow' | 'teal' | 'green' | 'mono' | 'purple' | 'red';
    size?: 'small' | 'medium' | 'large';
    bold?: boolean;
    /**
     * Border radius of the badge in rem.
     */
    borderRadius?: number;
    /**
     * if false, will use the react-router <Link> to redirect without refreshing
     */
    isExternalLink?: boolean;
  } & AnchorHTMLAttributes<HTMLAnchorElement>
>;

/**
 * Badge component
 *
 * https://www.figma.com/file/Ol2bVUv8SIB0MwRRJQ0Z61/Volt?node-id=3949%3A206672&t=aMxt4DfRwBTiX9iw-4
 */
export const Badge: FunctionComponent<BadgeProps> = ({
  icon,
  colour = 'yellow',
  size = 'small',
  bold,
  borderRadius,
  isExternalLink = false,
  children,
  ...anchorAttributes
}) => {
  const badgeContent = (
    <div
      className={`${styles['badge-component']} ${styles[`${colour}`]} ${styles[`${size}`]} ${icon ? '' : styles['no-icon']}`}
      style={{ '--border-radius': `${borderRadius ?? 4}rem` } as CSSProperties}
    >
      {icon && (
        <div className={styles['icon-wrapper']}>
          <Icon name={icon} className={styles.icon} width={24} height={24} />
        </div>
      )}
      <div className={`${styles.children} ${bold ? styles.bold : ''} ${icon ? '' : styles['hide-badge']}`}>{children}</div>
    </div>
  );

  if ((anchorAttributes as AnchorHTMLAttributes<HTMLAnchorElement>).href !== undefined) {
    if (isExternalLink) {
      return (
        <a className={styles['badge-component-wrapper']} {...(anchorAttributes as AnchorHTMLAttributes<HTMLAnchorElement>)}>
          {badgeContent}
        </a>
      );
    } else {
      const { href, ...aAttributes } = anchorAttributes as AnchorHTMLAttributes<HTMLAnchorElement>;
      return (
        <Link className={styles['badge-component-wrapper']} to={href ?? ''} {...aAttributes}>
          {badgeContent}
        </Link>
      );
    }
  } else {
    return <div className={styles['badge-component-wrapper']}>{badgeContent}</div>;
  }
};

export default Badge;
